import React, { useEffect, useMemo, useState } from 'react';
import type { Artist } from '../../../shared/types/entities';

interface ArtistAvatarProps {
  artist: Artist | null | undefined;
  size?: number; // px
  className?: string;
}

// Lightweight avatar with fallback to Spotify image via server route if no image_url
export const ArtistAvatar: React.FC<ArtistAvatarProps> = ({ artist, size = 40, className = '' }) => {
  const [imgUrl, setImgUrl] = useState<string | null>(artist?.image_url || null);
  const initial = useMemo(() => ((artist?.name || '').charAt(0) || '?').toUpperCase(), [artist?.name]);

  useEffect(() => {
    setImgUrl(artist?.image_url || null);
  }, [artist?.image_url]);

  useEffect(() => {
    if (!artist) return;
    if (imgUrl) return; // Already have an image
    const spotifyId = artist.spotify_id || extractSpotifyId(artist.spotify_url || '');
    if (!spotifyId) return;

    const controller = new AbortController();
    // Try server route that proxies Spotify (handles auth)
    fetch(`/api/spotify-artist?id=${encodeURIComponent(spotifyId)}`, { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) return null;
        const data = await res.json().catch(() => null);
        const url = data?.images?.[0]?.url || data?.artistProfile?.images?.[0]?.url || null;
        if (url) setImgUrl(url);
        return null;
      })
      .catch(() => null);
    return () => controller.abort();
  }, [imgUrl, artist?.spotify_id, artist?.spotify_url]);

  if (imgUrl) {
    return (
      <img
        src={imgUrl}
        alt={artist?.name || 'Artist'}
        style={{ width: size, height: size }}
        className={`rounded-full object-cover ${className}`}
        onError={() => setImgUrl(null)}
      />
    );
  }

  return (
    <div
      style={{ width: size, height: size }}
      className={`rounded-full bg-purple-500 dark:bg-purple-600 flex items-center justify-center ${className}`}
      aria-label={artist?.name || 'Artist'}
    >
      <span className="text-sm font-medium text-white">{initial}</span>
    </div>
  );
};

function extractSpotifyId(url: string): string | null {
  try {
    if (!url) return null;
    const u = new URL(url);
    // Formats like: https://open.spotify.com/artist/{id}
    const parts = u.pathname.split('/').filter(Boolean);
    if (parts[0] === 'artist' && parts[1]) return parts[1];
    return null;
  } catch {
    return null;
  }
}

export default ArtistAvatar;
